/* eslint-disable react/no-multi-comp */
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useFormikContext } from 'formik';
import { Button, Modal, Table, Tabs, message } from 'antd';
import { getSimilarContractsV2 } from 'store/commonSlice';

const SUB_BILLING_PER_ORDER = 'Per Order';

const money = (v) =>
	v === null || v === undefined || v === ''
		? '-'
		: `₹ ${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const plain = (v) =>
	v === null || v === undefined || v === '' ? '-' : Number(v).toLocaleString('en-IN');

const isPerOrder = (row) => row.billing_subtype === SUB_BILLING_PER_ORDER;

/**
 * Which rate columns apply to which billing type — mirrors the fields the Create/Edit forms
 * collect (VendorContractFormV2.js, ClientContractFormV2.js + .fields.js). A column that does
 * not apply renders "N/A"; one that applies but has no value renders "-".
 *
 * Column keys: hours = Working Hours, thr_day / thr_month = Threshold KM per Day / Month,
 * vehicle_cost, loader, associate, extra_km, extra_hr, charges_km = Charges / KM,
 * fixed_day = Fixed Rate / Day.
 *
 * VENDOR (sub billing type does not change the fields; Per Order only adds the rate list):
 *   Variable ............ vehicle_cost, loader, associate, extra_km (this is the per-km charge)
 *   Fixed + Variable .... hours, thr_day, thr_month, vehicle_cost, loader, associate,
 *                         extra_km, extra_hr
 *   Fixed Per Day ....... Fixed + Variable fields + fixed_day
 *
 * CLIENT:
 *   Fixed ............... hours, thr_day, thr_month, vehicle_cost, loader, associate
 *   Fixed + Variable .... Fixed fields + extra_km (per trip/km), extra_hr
 *   Fixed Per Day ....... Fixed + Variable fields + fixed_day
 *   Variable / Per Km ... Fixed + Variable fields + charges_km
 *   Variable / Per Order  vehicle_cost, loader, associate, charges_km
 *   (Fixed + Variable and Fixed Per Day with Per Order keep their usual fields and add rates.)
 *
 * Per Order contracts of any billing type additionally list Rate ID / Rate via "Show Rates".
 */
const FIXED_COLUMNS = ['hours', 'thr_day', 'thr_month', 'vehicle_cost', 'loader', 'associate'];
const FIXED_VARIABLE_COLUMNS = [...FIXED_COLUMNS, 'extra_km', 'extra_hr'];

const APPLICABLE_COLUMNS = {
	vendor: {
		Variable: ['vehicle_cost', 'loader', 'associate', 'extra_km'],
		'Fixed + Variable': FIXED_VARIABLE_COLUMNS,
		'Fixed Per Day': [...FIXED_VARIABLE_COLUMNS, 'fixed_day'],
	},
	client: {
		Fixed: FIXED_COLUMNS,
		'Fixed + Variable': FIXED_VARIABLE_COLUMNS,
		'Fixed Per Day': [...FIXED_VARIABLE_COLUMNS, 'fixed_day'],
		Variable: (row) =>
			isPerOrder(row)
				? ['vehicle_cost', 'loader', 'associate', 'charges_km']
				: [...FIXED_VARIABLE_COLUMNS, 'charges_km'],
	},
};

const isApplicable = (contractType, row, columnKey) => {
	const rule = APPLICABLE_COLUMNS[contractType]?.[row.billing_type];
	const keys = typeof rule === 'function' ? rule(row) : rule;
	return Boolean(keys?.includes(columnKey));
};

const cell = (contractType, columnKey, format) =>
	function (value, row) {
		return isApplicable(contractType, row, columnKey) ? (
			format(value)
		) : (
			<span style={{ color: '#bfbfbf' }}>N/A</span>
		);
	};

/** Cheapest vehicle cost first; contracts without one (e.g. Per Order) go last. */
const sortByVehicleCost = (rows) =>
	[...rows].sort((a, b) => {
		const av = a.vehicle_cost ?? Number.POSITIVE_INFINITY;
		const bv = b.vehicle_cost ?? Number.POSITIVE_INFINITY;
		return av - bv;
	});

const COLUMN_WIDTHS = {
	project: 130,
	city: 110,
	party: 180,
	vehicle_type: 170,
	billing_type: 130,
	billing_subtype: 120,
	vehicle_cost: 120,
	associate: 120,
	loader: 110,
	thr_day: 110,
	thr_month: 120,
	extra_km: 110,
	extra_hr: 130,
	charges_km: 110,
	fixed_day: 120,
	hours: 90,
	rates: 110,
};

const buildColumns = ({ contractType, showCity, onShowRates }) => {
	const isVendor = contractType === 'vendor';
	const columns = [
		{ title: 'Project', dataIndex: 'project_code', key: 'project' },
		...(showCity
			? [{ title: 'City', dataIndex: 'city', key: 'city', render: (v) => v || '-' }]
			: []),
		isVendor
			? { title: 'Vendor', dataIndex: 'vendor_name', key: 'party' }
			: { title: 'Client', dataIndex: 'client_name', key: 'party' },
		{ title: 'Vehicle Type', dataIndex: 'vehicle_type_name', key: 'vehicle_type' },
		{ title: 'Billing Type', dataIndex: 'billing_type', key: 'billing_type' },
		{ title: 'Sub Billing Type', dataIndex: 'billing_subtype', key: 'billing_subtype' },
		{
			title: 'Vehicle Cost',
			dataIndex: 'vehicle_cost',
			key: 'vehicle_cost',
			render: cell(contractType, 'vehicle_cost', money),
		},
		{
			title: 'Associate Cost',
			dataIndex: 'delivery_associate_cost',
			key: 'associate',
			render: cell(contractType, 'associate', money),
		},
		{
			title: 'Loader Cost',
			dataIndex: 'loader_cost',
			key: 'loader',
			render: cell(contractType, 'loader', money),
		},
		{
			title: 'Threshold KM / Day',
			dataIndex: 'threshold_km_per_day',
			key: 'thr_day',
			render: cell(contractType, 'thr_day', plain),
		},
		{
			title: 'Threshold KM / Month',
			dataIndex: 'threshold_km_per_month',
			key: 'thr_month',
			render: cell(contractType, 'thr_month', plain),
		},
		{
			title: 'Extra KM Charges',
			dataIndex: isVendor ? 'extra_charges_per_km' : 'extra_charges_per_trip',
			key: 'extra_km',
			render: cell(contractType, 'extra_km', money),
		},
		{
			title: 'Extra Hours Charges / Hour',
			dataIndex: 'extra_charges_per_hour',
			key: 'extra_hr',
			render: cell(contractType, 'extra_hr', money),
		},
		{
			title: 'Charges / KM',
			dataIndex: 'charges_per_km',
			key: 'charges_km',
			render: cell(contractType, 'charges_km', money),
		},
		{
			title: 'Fixed Rate / Day',
			dataIndex: 'fixed_rate_per_day',
			key: 'fixed_day',
			render: cell(contractType, 'fixed_day', money),
		},
		{
			title: 'Working Hours',
			dataIndex: 'fixed_hours_per_day',
			key: 'hours',
			render: cell(contractType, 'hours', plain),
		},
		{
			title: '',
			key: 'rates',
			render: (_, row) =>
				isPerOrder(row) ? (
					<Button type="link" size="small" onClick={() => onShowRates(row)}>
						Show Rates
					</Button>
				) : null,
		},
	];
	return columns.map((col) => ({ ...col, width: COLUMN_WIDTHS[col.key] }));
};

function MappedRatesModal({ contract, contractType, onClose }) {
	const partyName = contractType === 'vendor' ? contract?.vendor_name : contract?.client_name;
	return (
		<Modal
			open={Boolean(contract)}
			title={`Mapped Rates - ${partyName || ''}`}
			onCancel={onClose}
			footer={<Button onClick={onClose}>Close</Button>}
			width={520}
			zIndex={1100}
		>
			{contract && (
				<>
					<p style={{ marginBottom: 12 }}>
						Project: {contract.project_code || '-'} · Vehicle Type:{' '}
						{contract.vehicle_type_name || '-'} · Billing: {contract.billing_type} /{' '}
						{contract.billing_subtype}
					</p>
					<Table
						size="small"
						pagination={false}
						rowKey="rate_id"
						dataSource={contract.order_rates || []}
						columns={[
							{ title: 'Rate ID', dataIndex: 'rate_id' },
							{ title: 'Rate', dataIndex: 'rate', align: 'right', render: money },
						]}
					/>
				</>
			)}
		</Modal>
	);
}

MappedRatesModal.propTypes = {
	contract: PropTypes.object,
	contractType: PropTypes.string.isRequired,
	onClose: PropTypes.func.isRequired,
};

/**
 * "Similar Contracts" button + modal for the Vendor/Client contract forms.
 * Must render inside <Formik>; reads project / vehicle type / billing type from form values.
 */
export default function SimilarContracts({ contractType, excludeId }) {
	const dispatch = useDispatch();
	const { values } = useFormikContext();
	const [open, setOpen] = useState(false);
	const [scope, setScope] = useState('project');
	const [loading, setLoading] = useState(false);
	const [rows, setRows] = useState([]);
	const [ratesFor, setRatesFor] = useState(null);

	const { project_id, vehicle_type_id, billing_type, billing_subtype } = values;
	const canSearch = Boolean(project_id && vehicle_type_id);

	useEffect(() => {
		if (!open || !canSearch) return undefined;
		let cancelled = false;
		setLoading(true);
		dispatch(
			getSimilarContractsV2({
				contractType,
				scope,
				project_id,
				vehicle_type_id,
				billing_type,
				billing_subtype,
				exclude_id: excludeId,
			})
		)
			.then((contracts) => !cancelled && setRows(sortByVehicleCost(contracts)))
			.catch(() => {
				if (!cancelled) {
					setRows([]);
					message.error('Failed to load similar contracts');
				}
			})
			.finally(() => !cancelled && setLoading(false));
		return () => {
			cancelled = true;
		};
	}, [
		open,
		scope,
		canSearch,
		contractType,
		project_id,
		vehicle_type_id,
		billing_type,
		billing_subtype,
		excludeId,
		dispatch,
	]);

	const columns = useMemo(
		() => buildColumns({ contractType, showCity: scope === 'city', onShowRates: setRatesFor }),
		[contractType, scope]
	);

	const heading =
		scope === 'project' ? 'Contracts under the same project' : 'Contracts in the same city';

	return (
		<>
			<div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
				<Button
					onClick={() => setOpen(true)}
					disabled={!canSearch}
					title={canSearch ? undefined : 'Select a project and vehicle type first'}
				>
					Similar Contracts
				</Button>
			</div>
			<Modal
				open={open}
				title="Similar Contracts"
				onCancel={() => setOpen(false)}
				footer={null}
				width={1200}
				destroyOnClose
			>
				<Tabs
					activeKey={scope}
					onChange={setScope}
					items={[
						{ key: 'project', label: 'Project' },
						{ key: 'city', label: 'City' },
					]}
				/>
				<h4>{heading}</h4>
				<Table
					size="small"
					loading={loading}
					rowKey="id"
					columns={columns}
					dataSource={rows}
					pagination={false}
					tableLayout="fixed"
					scroll={{
						x: columns.reduce((sum, col) => sum + (col.width || 0), 0),
						y: 400,
					}}
					locale={{
						emptyText:
							scope === 'city'
								? 'No similar contracts found in this city (the project may have no city set)'
								: 'No similar contracts found in this project',
					}}
				/>
			</Modal>
			<MappedRatesModal
				contract={ratesFor}
				contractType={contractType}
				onClose={() => setRatesFor(null)}
			/>
		</>
	);
}

SimilarContracts.propTypes = {
	contractType: PropTypes.oneOf(['vendor', 'client']).isRequired,
	excludeId: PropTypes.string,
};
